import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  WebChatBotMessageModel,
  useWebChatBotMessages,
} from "@jugl-web/rest-api";
import { LoadingAnimation, DataLoadingWrapper } from "@jugl-web/ui-kit";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { VList, VListHandle } from "virtua";
import { isoToLocalDate, useTranslations, useDebounce } from "@jugl-web/utils";
import isToday from "date-fns/isToday";
import isThisYear from "date-fns/isThisYear";
import format from "date-fns/format";
import { useLanguage } from "@jugl-web/utils/i18n/EnhancedIntlProvider";
import { ChatDateHeader } from "@web-src/modules/chats/pages/ChatsPage/components/ChatWindow/components/ChatMessagesWindow/components/ChatDateHeader";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { ReactComponent as ChevronsDownIcon } from "../WhatsappChatMessageWindow/assets/chevrons-down.svg";
import { WebChatMessageItem } from "../WebChatMessageItem";

export interface WebChatMessageWindowHandle {
  scrollToBottom: () => void;
  addMessage: (
    message: WebChatBotMessageModel,
    forceScrollToBottom?: boolean
  ) => void;
}

type WebChatBotMessageItem =
  | { type: "message"; value: WebChatBotMessageModel }
  | { type: "date"; value: string };

export const WebChatMessageWindow = forwardRef<
  WebChatMessageWindowHandle,
  { conversationId: string }
>(({ conversationId }, ref) => {
  const { entityId } = useEntitySelectedProvider();
  const { t } = useTranslations();
  const { dateLocale } = useLanguage();
  const vListRef = useRef<VListHandle>(null);
  const [atBottom, setAtBottom] = useState(true);
  const debouncedAtBottom = useDebounce(atBottom, 500);
  const { incomingWebMessages$ } = useClientSupportPageContext();
  const [isInitialScrollInitialized, setIsInitialScrollInitialized] =
    useState(false);
  const isInitialScrollInitializationInProgress = useRef(false);
  const prevLenRef = useRef(0);
  const prevScrollOffsetRef = useRef(0);
  const wasLoadingMore = useRef(false);

  const {
    data: loadedMessages,
    isLoading,
    isLoadingMore,
    isError,
    hasMore,
    loadMore,
    reset,
  } = useWebChatBotMessages({
    entityId,
    conversationId,
    pageSize: 50,
  });

  const webChatMessageItems = useMemo(() => {
    if (!loadedMessages.length) return [];

    const result: WebChatBotMessageItem[] = [];
    const addedDates: string[] = [];

    loadedMessages.forEach((message) => {
      const date = isoToLocalDate(message.inserted_at);
      const dateStrAlt = format(date, "yyyy-MM-dd");

      if (!addedDates.includes(dateStrAlt)) {
        addedDates.push(dateStrAlt);
        const dateStr = isToday(date)
          ? t({
              id: "common.today",
              defaultMessage: "Today",
            })
          : format(date, isThisYear(date) ? "d MMMM" : "d MMMM yyyy", {
              locale: dateLocale,
            });

        result.push({ type: "date", value: dateStr });
      }

      result.push({ type: "message", value: message });
    });

    return result;
  }, [loadedMessages, t, dateLocale]);

  const scrollToBottom = useCallback(() => {
    vListRef.current?.scrollToIndex(webChatMessageItems.length, {
      align: "end",
      // LOOKS LIKE SMOOTH SCROLLING IS CURRENTLY BUGGED IN VIRTUA
      // https://github.com/inokawa/virtua/issues/715
    });
    setAtBottom(true);
  }, [webChatMessageItems.length]);

  // Reset when conversation changes
  useEffect(() => {
    setIsInitialScrollInitialized(false);
    reset();
  }, [conversationId, reset]);

  // Subscribe to incoming messages
  useEffect(() => {
    const subscription = incomingWebMessages$.subscribe((message) => {
      if (message.conv_id !== conversationId) return;

      if (atBottom) {
        setTimeout(scrollToBottom, 100);
      }
    });

    return () => subscription.unsubscribe();
  }, [
    incomingWebMessages$,
    conversationId,
    atBottom,
    webChatMessageItems.length,
    scrollToBottom,
  ]);

  // Initial scroll to bottom
  useEffect(() => {
    if (
      !isInitialScrollInitialized &&
      !isInitialScrollInitializationInProgress.current &&
      webChatMessageItems.length > 0 &&
      !isLoading
    ) {
      isInitialScrollInitializationInProgress.current = true;
      setTimeout(() => {
        vListRef.current?.scrollToIndex(webChatMessageItems.length - 1, {
          align: "end",
        });
        setIsInitialScrollInitialized(true);
        isInitialScrollInitializationInProgress.current = false;
      }, 100);
    }
  }, [webChatMessageItems.length, isLoading, isInitialScrollInitialized]);

  const addMessage = useCallback(
    (message: WebChatBotMessageModel, forceScrollToBottom?: boolean) => {
      if (forceScrollToBottom || atBottom) {
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    },
    [atBottom, scrollToBottom]
  );

  useImperativeHandle(ref, () => ({
    scrollToBottom,
    addMessage,
  }));

  const handleLoadMore = useCallback(async () => {
    if (hasMore && !isLoadingMore) {
      prevLenRef.current = webChatMessageItems.length;
      prevScrollOffsetRef.current = vListRef.current?.scrollOffset ?? 0;
      wasLoadingMore.current = true;
      await loadMore();
    }
  }, [hasMore, isLoadingMore, loadMore, webChatMessageItems.length]);

  useEffect(() => {
    if (isLoadingMore) {
      wasLoadingMore.current = true;
    }
  }, [isLoadingMore]);

  useEffect(() => {
    if (
      wasLoadingMore.current &&
      !isLoadingMore &&
      webChatMessageItems.length > prevLenRef.current
    ) {
      vListRef.current?.scrollToIndex(
        webChatMessageItems.length - prevLenRef.current,
        {
          align: "start",
          offset: prevScrollOffsetRef.current,
        }
      );
      wasLoadingMore.current = false;
    }
    prevLenRef.current = webChatMessageItems.length;
  }, [webChatMessageItems, isLoadingMore]);

  return (
    <div className="relative flex h-full flex-col bg-gray-100">
      <DataLoadingWrapper
        isError={isError}
        isFetching={false}
        isLoading={isLoading && loadedMessages.length === 0}
        onRetry={reset}
      >
        <VList
          ref={vListRef}
          reverse
          className="flex-1 pb-6"
          onScroll={(offset) => {
            if (
              !vListRef.current ||
              isLoadingMore ||
              !isInitialScrollInitialized
            ) {
              return;
            }
            const bottomOffset =
              offset -
              vListRef.current.scrollSize +
              vListRef.current.viewportSize;

            setAtBottom(bottomOffset > -5);
            // Load more messages when scrolling up
            if (offset < 150 && hasMore) {
              handleLoadMore();
            }
          }}
        >
          {isLoading && loadedMessages.length > 0 && hasMore && (
            <div className="flex justify-center py-4">
              <LoadingAnimation size="sm" />
            </div>
          )}
          {webChatMessageItems.map((item, index) => (
            <div
              key={item.type === "date" ? `date-${+index}` : item.value.id}
              className="mx-auto w-full max-w-[1000px]"
            >
              {item.type === "date" ? (
                <ChatDateHeader date={item.value} />
              ) : (
                <WebChatMessageItem
                  message={item.value}
                  messages={loadedMessages}
                />
              )}
            </div>
          ))}
        </VList>
      </DataLoadingWrapper>

      {!debouncedAtBottom && (
        <button
          type="button"
          onClick={() => scrollToBottom()}
          className="absolute right-4 bottom-4 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-primary-700 shadow-lg transition-opacity hover:opacity-90"
        >
          <ChevronsDownIcon className="text-white" />
        </button>
      )}
    </div>
  );
});

WebChatMessageWindow.displayName = "WebChatMessageWindow";
